import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Device } from '@capacitor/device';
import { App } from '@capacitor/app';
import { environment } from '@environments/environment';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { DBService } from './DBService.service'; // Tu servicio local
import { StoredDeviceInfo } from 'src/app/models/device-info';
import { Capacitor } from '@capacitor/core';
import packageInfo from '../../../../package.json';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  constructor(private http: HttpClient, private dbService: DBService) {}

  async publishDeviceInfo(fcmToken: string, forceUpdate = false): Promise<void> {
        if (Capacitor.getPlatform() === 'web') {
          console.warn('Device info not sent: running on web.');
          return;
        }
  
    try {
      const info = await Device.getInfo();
      const id = await Device.getId();
      const appInfo = await App.getInfo();

      const currentDeviceInfo: StoredDeviceInfo = {
        deviceName: info.name,
        deviceModel: info.model,
        deviceOS: info.operatingSystem,
        deviceVersion: info.osVersion,
        deviceUuid: id.identifier,
        deviceId: fcmToken || '',
        softwareVersion: appInfo.version,
        fcmToken: fcmToken|| '',
      };

      const savedDeviceInfo = await this.dbService.getDeviceInfo(); // fetch from local DB

if (!savedDeviceInfo || this.hasChanges(currentDeviceInfo, savedDeviceInfo) || forceUpdate) {
      await this.http
        .post(`${environment.resourceUrl}/api/contact/device/information`, currentDeviceInfo)
        .toPromise();

      await this.dbService.saveDeviceInfo(currentDeviceInfo);

      console.log('Device info sent to backend.');
    } else {
      console.log('Device info unchanged. Not sending to backend.');
    }
  } catch (error) {
    console.error('Error publishing device info:', error);
  }
  }

private hasChanges(current: any, saved: any): boolean {
  return (
    current.deviceName !== saved.deviceName ||
    current.deviceModel !== saved.deviceModel ||
    current.deviceOS !== saved.deviceOS ||
    current.deviceVersion !== saved.deviceVersion ||
    current.deviceUuid !== saved.deviceUuid ||
    current.softwareVersion !== saved.softwareVersion ||
    current.fcmToken !== saved.fcmToken
  );
}

  // Web equivalent of publishDeviceInfo (no native plugins). Same endpoint.
  async publishWebDeviceInfo(fcmToken: string, forceUpdate = false): Promise<void> {
    try {
      const currentDeviceInfo = this.buildWebDeviceInfo(fcmToken);
      const savedDeviceInfo = await this.dbService.getDeviceInfo();

      if (!savedDeviceInfo || this.hasChanges(currentDeviceInfo, savedDeviceInfo) || forceUpdate) {
        await this.http
          .post(`${environment.resourceUrl}/api/contact/device/information`, currentDeviceInfo)
          .toPromise();
        await this.dbService.saveDeviceInfo(currentDeviceInfo);
        console.log('Web device info sent to backend.');
      } else {
        console.log('Web device info unchanged. Not sending to backend.');
      }
    } catch (error) {
      console.error('Error publishing web device info:', error);
    }
  }

  buildWebDeviceInfo(fcmToken: string): StoredDeviceInfo {
    const name = this.getWebDeviceName();
    // Keep every string short: the backend MobileDevice columns are sized for
    // native values (e.g. "17.4"), so a long user-agent here breaks the insert.
    const cap = (v: string, n = 45) => (v || '').slice(0, n);
    return {
      deviceName: cap(name),
      deviceModel: 'Web',
      deviceOS: cap(name),
      deviceVersion: cap(this.getWebBrowserVersion()),
      deviceUuid: this.getOrCreateWebDeviceUuid(),
      deviceId: fcmToken || '',
      softwareVersion: cap(packageInfo.version, 20),
      fcmToken: fcmToken || '',
    };
  }

  private getWebBrowserVersion(): string {
    const ua = navigator.userAgent || '';
    const m = ua.match(/(Edg|OPR|Chrome|Firefox|Safari)\/([\d.]+)/);
    return m ? `${m[1]} ${m[2]}` : 'Web';
  }

  private getOrCreateWebDeviceUuid(): string {
    const KEY = 'fpt_web_device_uuid';
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = (window.crypto && typeof window.crypto.randomUUID === 'function')
        ? window.crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(KEY, id);
    }
    return id;
  }

  private getWebDeviceName(): string {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) return 'Android Device';
    if (/iPad|iPhone|iPod/.test(ua) && !('MSStream' in window)) return 'iOS Device';
    if (/windows phone/i.test(ua)) return 'Windows Phone';
    if (/windows/i.test(ua)) return 'Windows PC';
    if (/mac/i.test(ua)) return 'Mac';
    if (/linux/i.test(ua)) return 'Linux';
    return 'Web Device';
  }
}
